import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LayoutList, Save, RotateCcw, User, CalendarClock, Share2, CheckCircle2, Link as LinkIcon } from "lucide-react";
import { invalidateBlogDisplayCache } from "@/lib/blogDisplay";
import { BIBLIOGRAPHY_URL_STYLES } from "@/lib/bibliographyUrlStyle";
import BibliographyRef from "@/components/BibliographyRef";
import ShareSettingsPanel from "@/components/admin/ShareSettingsPanel";
import IconStylePicker from "@/components/admin/IconStylePicker";

const DEFAULTS = {
  toc_label: "Blog Contents",
  show_byline: true,
  show_added_date: true,
  show_share_bar: true,
  show_read_indicators: true,
  bibliography_url_render: true,
  bibliography_url_style: "subtle",
};

const TOGGLES = [
  { key: "show_byline",          icon: User,          title: "Author byline",     help: "The Author card that appears below the article." },
  { key: "show_added_date",      icon: CalendarClock, title: "\u201CAdded on\u201D date", help: "The publication date shown next to the Bookmark button." },
  { key: "show_share_bar",       icon: Share2,        title: "Share bar",         help: "The row of social share icons on every blog page." },
  { key: "show_read_indicators", icon: CheckCircle2,  title: "Read indicators",   help: "The green Read badge + Mark unread button (only shown to signed-in readers)." },
];

/** Tiny visual "chip" that mirrors what URLs will look like in this style.
 * Used inside the style picker radios so admins see the effect at a glance. */
function BibliographyStylePreview({ style }) {
  const label =
    style.displayAs === "bracket" ? "[link]" :
    style.displayAs === "icon"    ? "↗"       :
    "example.org/paper";
  return (
    <span className="text-slate-600 dark:text-slate-400 text-sm shrink-0">
      Smith 2024 <a href="#" onClick={(e) => e.preventDefault()} className={style.anchorClass}>{label}</a>
    </span>
  );
}

function Toggle({ icon: Icon, title, help, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer bg-white hover:bg-slate-50 transition-colors">
      <div className="w-8 h-8 grid place-items-center text-slate-500 shrink-0">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-slate-900 text-sm">{title}</div>
        <div className="text-xs text-slate-500 leading-relaxed">{help}</div>
      </div>
      <span className="text-xs uppercase tracking-widest text-slate-500 w-10 text-right">{checked ? "On" : "Off"}</span>
      <input type="checkbox"
             data-testid={testId}
             checked={checked}
             onChange={onChange}
             className="w-4 h-4 accent-sky-700"/>
    </label>
  );
}

export default function BlogDisplayPanel() {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/site-settings/blog-display").then(r => {
      const merged = { ...DEFAULTS, ...r.data };
      setSaved(merged);
      setDraft(merged);
    });

  useEffect(() => { load(); }, []);

  const save = async () => {
    const label = (draft.toc_label || "").trim();
    if (!label) { toast.error("Table of Contents name cannot be empty."); return; }
    if (label.length > 60) { toast.error("Keep it 60 characters or fewer."); return; }
    setSaving(true);
    try {
      const r = await api.put("/site-settings/blog-display", { ...draft, toc_label: label });
      const merged = { ...DEFAULTS, ...r.data };
      setSaved(merged); setDraft(merged);
      invalidateBlogDisplayCache();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetAll = () => setDraft(DEFAULTS);

  if (!draft) return <div className="mt-6 text-slate-500">Loading…</div>;

  const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
  const set = (patch) => setDraft(d => ({ ...d, ...patch }));

  return (
    <>
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading inline-flex items-center gap-2">
          <LayoutList className="w-4 h-4 text-sky-700"/>Blog display
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-6">
          Controls that affect how every blog page looks to readers.
        </p>

        {/* Table of Contents name */}
        <div className="max-w-xl">
          <label className="block font-heading font-semibold text-slate-900 text-sm mb-2" htmlFor="toc-label-input">
            Table of Contents name
          </label>
          <p className="text-xs text-slate-500 mb-3">
            The label that appears above the list of sections on every blog page — both the desktop sidebar and the mobile footer version.
          </p>
          <div className="flex items-center gap-2">
            <Input id="toc-label-input"
                   data-testid="blog-display-toc-label-input"
                   value={draft.toc_label}
                   maxLength={60}
                   onChange={e => set({ toc_label: e.target.value })}
                   placeholder="Blog Contents"
                   className="flex-1"/>
            <span className="text-xs text-slate-400 w-14 text-right tabular-nums">
              {(draft.toc_label || "").length}/60
            </span>
          </div>
        </div>

        {/* Visibility toggles */}
        <div className="mt-8 max-w-xl">
          <div className="font-heading font-semibold text-slate-900 text-sm mb-1">Show or hide on every blog page</div>
          <p className="text-xs text-slate-500 mb-4">
            Turn off any of these to keep the reading experience minimal, or on to give readers more context.
          </p>
          <div className="space-y-2">
            {TOGGLES.map(t => (
              <Toggle key={t.key}
                      icon={t.icon}
                      title={t.title}
                      help={t.help}
                      checked={!!draft[t.key]}
                      onChange={() => set({ [t.key]: !draft[t.key] })}
                      testId={`blog-display-toggle-${t.key}`}/>
            ))}
          </div>
        </div>

        {/* Bibliography URL rendering */}
        <div className="mt-8 max-w-xl" data-testid="bibliography-url-panel">
          <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 text-sm mb-1 inline-flex items-center gap-2">
            <LinkIcon className="w-4 h-4 text-sky-700 dark:text-sky-400"/>
            Bibliography URL display
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            How URLs appear inside bibliography references. Styles are intentionally quiet so citations don't dominate the reference list. Links always open in a new tab.
          </p>

          {/* Enable / disable toggle */}
          <label className="flex items-center gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors mb-4">
            <div className="w-8 h-8 grid place-items-center text-slate-500 dark:text-slate-400 shrink-0">
              <LinkIcon className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 text-sm">Turn URLs into clickable links</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Off: URLs render as plain text (never clickable). On: pick a visual style below.
              </div>
            </div>
            <span className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 w-10 text-right">
              {draft.bibliography_url_render ? "On" : "Off"}
            </span>
            <input
              type="checkbox"
              data-testid="bibliography-url-enabled"
              checked={!!draft.bibliography_url_render}
              onChange={() => set({ bibliography_url_render: !draft.bibliography_url_render })}
              className="w-4 h-4 accent-sky-700"
            />
          </label>

          {/* Style picker */}
          <div className={`space-y-2 transition-opacity ${draft.bibliography_url_render ? "" : "opacity-50 pointer-events-none"}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Visual style</div>
            <div className="grid gap-2" role="radiogroup" aria-label="Bibliography URL visual style">
              {BIBLIOGRAPHY_URL_STYLES.map(opt => {
                const active = draft.bibliography_url_style === opt.id;
                return (
                  <label
                    key={opt.id}
                    data-testid={`bibliography-url-style-${opt.id}`}
                    className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                      active
                        ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-700"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    }`}
                  >
                    <input
                      type="radio"
                      name="bibliography_url_style"
                      value={opt.id}
                      checked={active}
                      onChange={() => set({ bibliography_url_style: opt.id })}
                      className="w-4 h-4 accent-sky-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 text-sm">{opt.label}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{opt.hint}</div>
                    </div>
                    <BibliographyStylePreview style={opt}/>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Live preview */}
          <div className="mt-5 border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Live preview — bibliography item
            </div>
            <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded p-4">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400 mb-3">
                Bibliography
              </div>
              <ol className="space-y-2 list-decimal pl-6 text-sm text-slate-700 dark:text-slate-300">
                <li data-testid="bibliography-url-live-preview-1">
                  <BibliographyRef
                    text="National Hospice and Palliative Care Organization. Facts and figures 2024. https://www.nhpco.org/factsfigures"
                    enabled={draft.bibliography_url_render}
                    styleId={draft.bibliography_url_style}
                  />
                </li>
                <li data-testid="bibliography-url-live-preview-2">
                  <BibliographyRef
                    text="Casarett D, et al. Improving hospice referral. JAMA. 2005;293(2):211-217 (https://doi.org/10.1001/jama.293.2.211)."
                    enabled={draft.bibliography_url_render}
                    styleId={draft.bibliography_url_style}
                  />
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Save / Reset actions */}
        <div className="mt-6 flex items-center gap-2">
          <Button data-testid="blog-display-save"
                  onClick={save}
                  disabled={!dirty || saving}
                  className="bg-sky-700 hover:bg-sky-800">
            <Save className="w-4 h-4 mr-2"/>{saving ? "Saving…" : "Save changes"}
          </Button>
          <Button data-testid="blog-display-reset"
                  variant="outline"
                  onClick={resetAll}
                  disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2"/>Reset all to defaults
          </Button>
          {dirty && !saving && (
            <span className="text-xs text-amber-700 ml-2">Unsaved changes</span>
          )}
        </div>

        {/* Live preview */}
        <div className="mt-8 max-w-xl border border-dashed border-slate-300 bg-slate-50/60 rounded-lg p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
            Live preview — blog page top strip
          </div>
          <div className="bg-white border border-slate-200 rounded p-4">
            <div className="text-xs uppercase tracking-widest text-sky-700 mb-1">WHAT IS HOSPICE?</div>
            <div className="font-heading font-semibold text-slate-900 text-lg mb-3">Sample Blog Title</div>
            <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 border border-slate-300 rounded px-2 py-1">Bookmark</span>
              {draft.show_added_date && <span>Added 7/7/2026</span>}
              {draft.show_read_indicators && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3"/>Read</span>}
              {draft.show_share_bar && (
                <span className="ml-auto inline-flex items-center gap-1">
                  <span className="uppercase tracking-widest text-slate-500 mr-1">Share</span>
                  <Share2 className="w-3.5 h-3.5 text-slate-400"/>
                </span>
              )}
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-slate-500 mb-1">
              {draft.toc_label?.trim() || DEFAULTS.toc_label}
            </div>
            <div className="border-l border-slate-200 pl-3 text-sm text-slate-700 space-y-0.5">
              <div className="text-sky-700 font-medium">Section one</div>
              <div>Section two</div>
            </div>
            {draft.show_byline && (
              <div className="mt-4 pt-3 border-t border-slate-200">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Author</div>
                <div className="font-heading font-semibold text-slate-900 text-sm">Dr. Sample Author</div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Nested: blog share bar configuration lives here since it's a blog-page display concern */}
    {draft.show_share_bar && (
      <div className="mt-2 pl-1 text-xs text-slate-500">
        Share bar is enabled above. Configure which platforms appear ↓
      </div>
    )}
    <ShareSettingsPanel/>
    <div className="mt-6"><IconStylePicker/></div>
    </>
  );
}
